import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Edit, Truck, Loader2 } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { CARRIERS } from '../../utils/shippingCarriers';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusForm, setStatusForm] = useState({
    status: '',
    shippingCarrierCode: 'yurtici',
    shippingCarrierName: '',
    trackingNumber: '',
    reason: ''
  });

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await api.get('/orders/admin/all');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const openStatusModal = (order) => {
    setSelectedOrder(order);
    setStatusForm({
      status: order.order_status || 'beklemede',
      shippingCarrierCode: order.shipping_carrier_code || 'yurtici',
      shippingCarrierName: order.shipping_carrier_code === 'other' ? order.shipping_carrier || '' : '',
      trackingNumber: order.tracking_number || '',
      reason: ''
    });
    setShowModal(true);
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;

    if (statusForm.status === 'kargoda') {
      if (!statusForm.trackingNumber.trim()) {
        toast.error('Takip numarası girin');
        return;
      }
      if (statusForm.shippingCarrierCode === 'other' && !statusForm.shippingCarrierName.trim()) {
        toast.error('Kargo firması adını yazın');
        return;
      }
    }

    setStatusSaving(true);
    try {
      await api.put(`/orders/${selectedOrder.id}/status`, {
        status: statusForm.status,
        trackingNumber: statusForm.trackingNumber.trim(),
        shippingCarrierCode: statusForm.shippingCarrierCode,
        shippingCarrierName: statusForm.shippingCarrierName.trim(),
        reason: statusForm.reason.trim()
      });
      toast.success('Sipariş durumu güncellendi');
      loadOrders();
      setShowModal(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Durum güncellenemedi:', error);
      toast.error(error.response?.data?.message || 'Durum güncellenemedi');
    } finally {
      setStatusSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'beklemede': return 'bg-yellow-100 text-yellow-800';
      case 'onaylandi': return 'bg-blue-100 text-blue-800';
      case 'hazirlaniyor': return 'bg-purple-100 text-purple-800';
      case 'kargoda': return 'bg-indigo-100 text-indigo-800';
      case 'teslim_edildi': return 'bg-green-100 text-green-800';
      case 'iptal': return 'bg-red-100 text-red-800';
      // Eski değerler için backward compatibility
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'beklemede': return 'Beklemede';
      case 'onaylandi': return 'Onaylandı';
      case 'hazirlaniyor': return 'Hazırlanıyor';
      case 'kargoda': return 'Kargoda';
      case 'teslim_edildi': return 'Teslim Edildi';
      case 'iptal': return 'İptal Edildi';
      // Eski değerler için backward compatibility
      case 'pending': return 'Beklemede';
      case 'processing': return 'İşleniyor';
      case 'shipped': return 'Kargoda';
      case 'delivered': return 'Teslim Edildi';
      case 'cancelled': return 'İptal Edildi';
      default: return status || 'Bilinmeyen';
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.user?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || order.order_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white p-4 rounded-lg shadow">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Sipariş Yönetimi</h1>
        <p className="mt-2 text-gray-600">Tüm siparişleri görüntüleyin ve yönetin</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Sipariş ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Tüm Durumlar</option>
            <option value="beklemede">Beklemede</option>
            <option value="onaylandi">Onaylandı</option>
            <option value="hazirlaniyor">Hazırlanıyor</option>
            <option value="kargoda">Kargoda</option>
            <option value="teslim_edildi">Teslim Edildi</option>
            <option value="iptal">İptal Edildi</option>
          </select>

          <button
            onClick={loadOrders}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtrele
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sipariş No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Müşteri
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tutar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tarih
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{order.order_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.user?.name || order.shipping_name || 'Misafir Kullanıcı'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₺{parseFloat(order.total || order.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.order_status)}`}>
                      {getStatusText(order.order_status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.createdAt).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => openStatusModal(order)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowDetailModal(true);
                      }}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Update Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 pt-16">
          <form
            onSubmit={handleStatusSubmit}
            className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl"
          >
            <div className="border-b border-slate-100 px-6 py-4">
              <h3 className="text-lg font-semibold text-slate-900">Sipariş durumu</h3>
              <p className="text-sm text-slate-500 mt-1">#{selectedOrder.order_number}</p>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Durum</label>
                <select
                  value={statusForm.status}
                  onChange={(e) => setStatusForm((f) => ({ ...f, status: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                >
                  {['beklemede', 'onaylandi', 'hazirlaniyor', 'kargoda', 'teslim_edildi', 'iptal'].map((status) => (
                    <option key={status} value={status}>
                      {getStatusText(status)}
                    </option>
                  ))}
                </select>
              </div>

              {statusForm.status === 'kargoda' && (
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/80 p-4 space-y-3">
                  <p className="text-sm font-medium text-indigo-900 flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Kargo bilgileri (müşteriye mail gider)
                  </p>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">Kargo firması *</label>
                    <select
                      value={statusForm.shippingCarrierCode}
                      onChange={(e) =>
                        setStatusForm((f) => ({ ...f, shippingCarrierCode: e.target.value }))
                      }
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                      required
                    >
                      {CARRIERS.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {statusForm.shippingCarrierCode === 'other' && (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-600">Firma adı *</label>
                      <input
                        type="text"
                        value={statusForm.shippingCarrierName}
                        onChange={(e) =>
                          setStatusForm((f) => ({ ...f, shippingCarrierName: e.target.value }))
                        }
                        placeholder="Örn: Kolay Gelsin"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">Takip numarası *</label>
                    <input
                      type="text"
                      value={statusForm.trackingNumber}
                      onChange={(e) =>
                        setStatusForm((f) => ({ ...f, trackingNumber: e.target.value }))
                      }
                      placeholder="Kargo barkod / takip no"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                      required
                    />
                  </div>
                </div>
              )}

              {statusForm.status === 'iptal' && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">İptal nedeni (isteğe bağlı)</label>
                  <textarea
                    rows={2}
                    value={statusForm.reason}
                    onChange={(e) => setStatusForm((f) => ({ ...f, reason: e.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100"
              >
                Vazgeç
              </button>
              <button
                type="submit"
                disabled={statusSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-5 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"
              >
                {statusSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                Kaydet
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  Sipariş Detayları - #{selectedOrder.order_number}
                </h3>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedOrder(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sol Taraf - Müşteri ve Teslimat Bilgileri */}
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Müşteri Bilgileri
                      {!selectedOrder.user && (
                        <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">Misafir</span>
                      )}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="font-medium">Ad Soyad:</span> 
                        <span className="ml-2">{selectedOrder.user?.name || selectedOrder.shipping_name || 'Bilinmeyen'}</span>
                      </p>
                      <p>
                        <span className="font-medium">E-posta:</span> 
                        <span className="ml-2">{selectedOrder.user?.email || selectedOrder.guest_email || '-'}</span>
                      </p>
                      <p>
                        <span className="font-medium">Telefon:</span> 
                        <span className="ml-2">{selectedOrder.user?.phone || selectedOrder.shipping_phone || '-'}</span>
                      </p>
                      {!selectedOrder.user && selectedOrder.guest_email && (
                        <p className="text-xs text-gray-500 italic mt-2">
                          Bu sipariş misafir kullanıcı tarafından verilmiştir
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Teslimat Adresi</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Adres:</span> {selectedOrder.shipping_address || '-'}</p>
                      <p><span className="font-medium">Şehir:</span> {selectedOrder.shipping_city || '-'}</p>
                      <p><span className="font-medium">İlçe:</span> {selectedOrder.shipping_state || '-'}</p>
                      {selectedOrder.shipping_zip_code && (
                        <p><span className="font-medium">Posta Kodu:</span> {selectedOrder.shipping_zip_code}</p>
                      )}
                      <p><span className="font-medium">Telefon:</span> {selectedOrder.shipping_phone || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Sağ Taraf - Sipariş ve Ödeme Bilgileri */}
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Sipariş Bilgileri</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Sipariş No:</span> #{selectedOrder.order_number}</p>
                      <p><span className="font-medium">Sipariş Tarihi:</span> {new Date(selectedOrder.created_at || selectedOrder.createdAt).toLocaleString('tr-TR')}</p>
                      <p><span className="font-medium">Sipariş Durumu:</span> 
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(selectedOrder.order_status)}`}>
                          {getStatusText(selectedOrder.order_status)}
                        </span>
                      </p>
                      <p><span className="font-medium">Ödeme Durumu:</span> 
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                          selectedOrder.payment_status === 'odendi' ? 'bg-green-100 text-green-800' :
                          selectedOrder.payment_status === 'iptal' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {selectedOrder.payment_status === 'odendi' ? 'Ödendi' :
                           selectedOrder.payment_status === 'iptal' ? 'İptal' : 'Beklemede'}
                        </span>
                      </p>
                      {selectedOrder.payment_id && (
                        <p><span className="font-medium">Ödeme ID:</span> {selectedOrder.payment_id}</p>
                      )}
                      {(selectedOrder.shipping_carrier || selectedOrder.tracking_number) && (
                        <p>
                          <span className="font-medium">Kargo:</span>{' '}
                          {selectedOrder.shipping_carrier || '—'}
                          {selectedOrder.tracking_number
                            ? ` · ${selectedOrder.tracking_number}`
                            : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Tutar Bilgileri</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Ara Toplam:</span>
                        <span>₺{parseFloat(selectedOrder.subtotal || selectedOrder.subtotal_amount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Kargo:</span>
                        <span>{parseFloat(selectedOrder.shipping_cost || 0) > 0 ? `₺${parseFloat(selectedOrder.shipping_cost || 0).toFixed(2)}` : 'Ücretsiz'}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg border-t pt-2">
                        <span>Toplam:</span>
                        <span>₺{parseFloat(selectedOrder.total || selectedOrder.total_amount || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ürünler */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Sipariş Edilen Ürünler</h4>
                  <div className="space-y-3">
                    {selectedOrder.items.map((item, index) => {
                      // Görsel yolu kontrolü - object veya string olabilir
                      let productImage = item.product?.images?.[0] || item.image || null;
                      if (productImage) {
                        if (typeof productImage === 'object' && productImage?.url) {
                          productImage = productImage.url;
                        } else if (typeof productImage !== 'string') {
                          productImage = null;
                        }
                      }
                      
                      return (
                      <div key={index} className="bg-white p-3 rounded-lg flex items-center space-x-4">
                        {productImage && (
                          <img
                            src={typeof productImage === 'string' && productImage.startsWith('http') 
                              ? productImage 
                              : typeof productImage === 'string' && productImage.startsWith('/')
                                ? productImage
                                : `/uploads/${productImage}`}
                            alt={item.product?.name || item.name}
                            className="w-16 h-16 object-cover rounded"
                            onError={(e) => {
                              e.target.src = '/images/placeholder.jpg';
                            }}
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.product?.name || item.name || 'Ürün'}</p>
                          <p className="text-sm text-gray-600">Adet: {item.quantity || 1}</p>
                          {item.product?.sku && (
                            <p className="text-xs text-gray-500">SKU: {item.product.sku}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            ₺{parseFloat((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                          </p>
                          {item.quantity > 1 && (
                            <p className="text-xs text-gray-500">₺{parseFloat(item.price || 0).toFixed(2)} / adet</p>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedOrder.notes && (
                <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Sipariş Notları</h4>
                  <p className="text-sm text-gray-700">{selectedOrder.notes}</p>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setShowModal(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Durum Güncelle
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedOrder(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;